import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import comptabiliteService, { GrandLivre as GrandLivreData } from '../../api/comptabilite';
import { formatTND, formatDate } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

const GrandLivre: React.FC = () => {
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [compte, setCompte] = useState('');
  const [data, setData] = useState<GrandLivreData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await comptabiliteService.grandLivre(startDate, endDate, compte || undefined));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Grand-livre</h1>
        <p className="text-sm text-gray-500">Détail des mouvements par compte avec solde progressif.</p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Du</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Au</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Compte (préfixe)</label>
          <input value={compte} onChange={(e) => setCompte(e.target.value)} placeholder="ex. 411"
                 className="rounded-lg border border-gray-200 px-3 py-2 w-40" />
        </div>
        <button onClick={load}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
          Filtrer
        </button>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-10">Chargement…</div>
      ) : !data || data.comptes.length === 0 ? (
        <div className="text-center text-gray-400 py-10">Aucun mouvement sur la période.</div>
      ) : data.comptes.map((c) => (
        <div key={c.compteNumero} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="font-semibold">
              <span className="font-mono">{c.compteNumero}</span> — {c.libelle}
            </div>
            <div className="text-sm text-gray-600">
              Solde : <span className="font-semibold">{formatTND(c.solde)}</span>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Date</th>
                <th className="text-left px-4 py-2 font-medium">Journal</th>
                <th className="text-left px-4 py-2 font-medium">Pièce</th>
                <th className="text-left px-4 py-2 font-medium">Libellé</th>
                <th className="text-right px-4 py-2 font-medium">Débit</th>
                <th className="text-right px-4 py-2 font-medium">Crédit</th>
                <th className="text-right px-4 py-2 font-medium">Solde</th>
              </tr>
            </thead>
            <tbody>
              {c.mouvements.map((m, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-2 whitespace-nowrap">{formatDate(m.date)}</td>
                  <td className="px-4 py-2 font-mono">{m.journalCode}</td>
                  <td className="px-4 py-2 font-mono">{m.ecritureNumero}</td>
                  <td className="px-4 py-2">{m.libelle}</td>
                  <td className="px-4 py-2 text-right">{m.debit ? formatTND(m.debit) : '—'}</td>
                  <td className="px-4 py-2 text-right">{m.credit ? formatTND(m.credit) : '—'}</td>
                  <td className="px-4 py-2 text-right">{formatTND(m.soldeProgressif)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 font-semibold bg-gray-50">
                <td className="px-4 py-2" colSpan={4}>Totaux</td>
                <td className="px-4 py-2 text-right">{formatTND(c.totalDebit)}</td>
                <td className="px-4 py-2 text-right">{formatTND(c.totalCredit)}</td>
                <td className="px-4 py-2 text-right">{formatTND(c.solde)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}
    </div>
  );
};

export default GrandLivre;
